import type { UserListItem } from '@planuze/admin-contracts';
import {
  Button,
  EmptyState,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@planuze/ui';
import { Users } from 'lucide-react';
import { useUsersContent } from '../../content/sections/users.js';
import { fmtDate } from '../../lib/format.js';

interface Props {
  readonly items: readonly UserListItem[];
  readonly onRowClick: (id: string) => void;
  /** Há busca/filtro ativos — diferencia "sem resultados" de "sem itens" (ADR-0700/§6). */
  readonly hasFilters: boolean;
  readonly onClearFilters: () => void;
}

/* Tabela de usuários sobre o primitive `Table` de @planuze/ui (ADR-0700). O e-mail é um
   `<button>` (navegação acessível por teclado). */
export function UsersTable({ items, onRowClick, hasFilters, onClearFilters }: Props) {
  const copy = useUsersContent();
  if (items.length === 0) {
    return hasFilters ? (
      <EmptyState
        icon={<Users className="size-6" />}
        title={copy.common.noResultsTitle}
        description={copy.table.emptyFilteredDescription}
        primaryAction={
          <Button variant="outline" onClick={onClearFilters}>
            {copy.common.clearFilters}
          </Button>
        }
      />
    ) : (
      <EmptyState
        icon={<Users className="size-6" />}
        title={copy.table.emptyTitle}
        description={copy.table.emptyDescription}
      />
    );
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{copy.table.columnEmail}</TableHead>
          <TableHead>{copy.table.columnName}</TableHead>
          <TableHead>{copy.table.columnStatus}</TableHead>
          <TableHead>{copy.table.columnCreatedAt}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((user) => (
          <TableRow key={user.id}>
            <TableCell>
              <button
                type="button"
                className="font-medium text-foreground transition-colors hover:underline"
                onClick={() => onRowClick(user.id)}
              >
                {user.email}
              </button>
            </TableCell>
            <TableCell className="text-muted-foreground">{user.name ?? '—'}</TableCell>
            <TableCell>
              <StatusBadge variant={user.status === 'blocked' ? 'destructive' : 'success'} dot>
                {user.status === 'blocked' ? copy.table.statusBlocked : copy.table.statusActive}
              </StatusBadge>
            </TableCell>
            <TableCell className="text-muted-foreground">{fmtDate(user.createdAt)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
